import { useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Camera, Check, ChevronRight, Loader2, RotateCcw, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { estateDraftStatus, estateDraftPhotoProof } from "../../lib/api";
import { tr } from "../../lib/i18n";

// Builds the ordered list of pages to photograph from the draft: title page, each article, then signature & notary pages.
export const planPages = (draft, kind) => {
  const secs = (draft?.sections || []).map((s) => s.heading).filter(Boolean);
  const pages = [{ key: "title", label: "Title page", hint: "The first page with the document title and your name." }];
  secs.forEach((h, i) => pages.push({ key: `s${i}`, label: h, hint: "Photograph the page(s) where this section appears — one photo per page." }));
  pages.push({ key: "sign", label: "Signature page", hint: "Everyone's ink signatures and the date. Make sure every line is filled." });
  if (["simple_will", "mirror_wills", "financial_poa", "living_trust", "joint_trust"].includes(kind)) pages.push({ key: "notary", label: "Notary / self-proving page", hint: "The notary's stamp, seal and commission expiry." });
  return pages;
};

export const PhotoGuide = ({ kind, open, onClose }) => {
  const nav = useNavigate();
  const qc = useQueryClient();
  const input = useRef(null);
  const { data: draft } = useQuery({ queryKey: ["estate-draft", kind], queryFn: () => estateDraftStatus(kind), enabled: open });
  const pages = useMemo(() => planPages(draft, kind), [draft, kind]);
  const [shots, setShots] = useState({});
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const taken = Object.keys(shots).length;
  const done = taken >= pages.length;
  const current = pages[Math.min(step, pages.length - 1)];

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setShots((s) => ({ ...s, [current.key]: { file: f, url: URL.createObjectURL(f) } }));
    const next = pages.findIndex((p, i) => i > step && !shots[p.key]);
    setStep(next === -1 ? Math.min(step + 1, pages.length) : next);
    e.target.value = "";
  };
  const retake = (i) => setStep(i);
  const file = async () => {
    setBusy(true);
    try {
      const files = pages.filter((p) => shots[p.key]).map((p) => shots[p.key].file);
      const r = await estateDraftPhotoProof(kind, files);
      toast.success(`${r.pages} ${tr("pages filed as the signed copy — checking them now")}`);
      qc.invalidateQueries({ queryKey: ["documents"] });
      qc.invalidateQueries({ queryKey: ["draft-compare"] });
      onClose();
      nav(`/library/drafts/${kind}?step=sign`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || tr("Could not file the photos"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="photo-guide">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">{tr("Photograph the signed pages")}</DialogTitle>
          <DialogDescription>{tr("One photo per page, in order. Fill the frame, avoid glare, and keep the page flat.")}</DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-1.5 flex-wrap" data-testid="photo-guide-progress">
          {pages.map((p, i) => <button key={p.key} type="button" onClick={() => retake(i)} title={p.label} className={`h-2.5 w-2.5 rounded-full ${shots[p.key] ? "bg-emerald-500" : i === step ? "bg-primary" : "bg-muted-foreground/30"}`} data-testid={`photo-guide-dot-${i}`} />)}
          <span className="ml-2 text-xs text-muted-foreground" data-testid="photo-guide-count">{taken} / {pages.length} {tr("captured")}</span>
        </div>
        {!done || step < pages.length ? (
          <div className="rounded-2xl border border-primary/30 bg-primary/5 p-4" data-testid="photo-guide-step">
            <div className="text-[10px] uppercase tracking-widest font-mono text-primary">{tr("Next")} · {tr("page")} {Math.min(step, pages.length - 1) + 1} {tr("of")} {pages.length}</div>
            <div className="font-medium text-lg mt-0.5" data-testid="photo-guide-label">{tr(current.label)}</div>
            <p className="text-xs text-muted-foreground mt-1">{tr(current.hint)}</p>
            {shots[current.key] && <img src={shots[current.key].url} alt="" className="mt-3 h-24 rounded-lg border border-border object-cover" />}
            <div className="mt-3 flex flex-wrap gap-2">
              <input ref={input} type="file" accept="image/*" capture="environment" className="hidden" onChange={onFile} data-testid="photo-guide-input" />
              <Button onClick={() => input.current?.click()} className="rounded-full gap-1.5" data-testid="photo-guide-capture"><Camera className="h-4 w-4" /> {shots[current.key] ? tr("Retake this page") : tr("Take photo")}</Button>
              {!shots[current.key] && <Button variant="ghost" onClick={() => setStep((s) => Math.min(s + 1, pages.length))} className="rounded-full gap-1 text-muted-foreground" data-testid="photo-guide-skip">{tr("Skip")} <ChevronRight className="h-4 w-4" /></Button>}
            </div>
          </div>
        ) : null}
        {done && (
          <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-4 flex items-center gap-3" data-testid="photo-guide-done">
            <Check className="h-5 w-5 text-emerald-600" />
            <div className="text-sm">{tr("All pages captured.")} <span className="text-muted-foreground">{tr("File them as the signed copy — the vault checks pages and signatures automatically.")}</span></div>
          </div>
        )}
        {taken > 0 && (
          <div className="flex flex-wrap gap-2" data-testid="photo-guide-thumbs">
            {pages.filter((p) => shots[p.key]).map((p) => <button key={p.key} type="button" onClick={() => retake(pages.indexOf(p))} className="relative" title={`${p.label} — retake`}><img src={shots[p.key].url} alt="" className="h-14 w-11 rounded-md border border-border object-cover" /><RotateCcw className="absolute -right-1 -bottom-1 h-3.5 w-3.5 rounded-full bg-background p-0.5 text-muted-foreground" /></button>)}
          </div>
        )}
        <div className="flex items-center justify-between gap-2 pt-1">
          <Button variant="ghost" onClick={onClose} className="rounded-full gap-1 text-muted-foreground"><X className="h-4 w-4" /> {tr("Cancel")}</Button>
          <Button onClick={file} disabled={busy || taken === 0} className="rounded-full gap-1.5 bg-primary hover:bg-primary/90" data-testid="photo-guide-file">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} {done ? tr("File the signed copy") : `${tr("File")} ${taken} ${tr("so far")}`}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
